import { Position } from '@busybusy/webapp-react-ui';
import { useQuery } from '@tanstack/react-query';
import {
  MemberClockStateType,
  MemberPermissionFilter,
  MemberSort,
  PositionFilter,
  QueryMemberPickQuery,
  QueryMemberPickQueryVariables,
} from '__generated__/graphql';
import classNames from 'classnames';
import DropDownPicker from 'components/foundation/pickers/DropDownPicker/DropDownPicker';
import SimplePickerRow from 'components/foundation/pickers/SimplePickerRow/SimplePickerRow';
import { useDebounce } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { getApolloMemberNameSearch } from 'hooks/models/member/useApolloMemberNameSearch';
import { getApolloMemberNameSort } from 'hooks/models/member/useApolloMemberNameSort';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import useEmployeeNameFormat from 'hooks/settings/local-settings/useEmployeeNameFormat';
import _, { first } from 'lodash';
import { useRef, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { getFullNameFromMember } from 'utils/stringUtils';
import { membersQuery } from './employee-picker-queries';
import useEmployeeNameFormatter from 'hooks/ui/useEmployeeNameFormatter';

export interface IEmployeePickerProps {
  value: string | null;
  onSelect: (memberId: string | null) => void;
  // Ids are filtered out at the graphql level
  blacklistedIds?: string[];
  permissions?: MemberPermissionFilter;
  positionFilter?: PositionFilter;
  minWidth?: string; // has default
  className?: ClassName;
  placeholder?: string;
  error?: boolean;
  position?: Position;
  searchArchived?: boolean;
  includeClockedIn?: boolean;
  includedIds?: string[];
}

export type EmployeePickerMember = NonNullArrayField<QueryMemberPickQuery['members']>;

function EmployeePicker({
  value,
  onSelect,
  blacklistedIds,
  permissions,
  positionFilter,
  minWidth = '350px',
  className,
  placeholder,
  error,
  position,
  searchArchived,
  includeClockedIn,
  includedIds,
}: IEmployeePickerProps) {
  const [employeeNameFormat] = useEmployeeNameFormat();
  const employeeNameFormatter = useEmployeeNameFormatter();
  const [searchValue, setSearchValue] = useState('');
  const searchDebounce = useDebounce(searchValue, 250);

  const graphqlClient = useGraphQLClient();

  const [isOpen, setIsOpen] = useState(false);

  function onClose() {
    setIsOpen(false);
    setSearchValue('');
  }

  function handleSelect(model: EmployeePickerMember | null) {
    onSelect(model ? model.id : null);
  }

  const baseQueryKey = useReactQueryBaseKey();
  const { data: selectedMember } = useQuery({
    queryKey: [...baseQueryKey, 'employee-picker-by-id', value],
    queryFn: async () => {
      if (!value) {
        return null;
      }

      const member = _.find(data, (model) => model.id === value);

      if (member) {
        return employeeNameFormatter(member.firstName ?? '', member.lastName ?? '');
      } else {
        const memberResult = await graphqlClient.request<QueryMemberPickQuery, QueryMemberPickQueryVariables>({
          document: membersQuery,
          variables: {
            filter: { id: { equal: value } },
          },
        });

        const target = first(memberResult?.members);
        return target ? employeeNameFormatter(target.firstName ?? '', target.lastName ?? '') : null;
      }
    },
    initialData: null,
    enabled: !!value,
  });

  const variables: QueryMemberPickQueryVariables = {
    filter: {
      archivedOn: searchArchived ? { isNull: false } : { isNull: true },
      search: searchDebounce
        ? getApolloMemberNameSearch(employeeNameFormat ?? 'FIRST_LAST', searchDebounce)
        : undefined,
      // NOTE: Carry-over prerefactor not sure how a blacklist and whitelist should interact together just keeping
      // similar functionality
      id: blacklistedIds ? { doesNotContain: blacklistedIds } : includedIds ? { contains: includedIds } : undefined,
      permissions: permissions,
      // NOTE: undefined gets ignored and true should not be filtered so it's also undefined
      clockState: includeClockedIn === false ? { state: MemberClockStateType.ClockedOut } : undefined,
      position: positionFilter,
    },
    // TODO: Replace cast when util is reworked
    sort: getApolloMemberNameSort(employeeNameFormat ?? 'FIRST_LAST', 'asc') as MemberSort,
  };

  async function getMembers(after: string | null, first: number): Promise<EmployeePickerMember[]> {
    const pagedVariables: QueryMemberPickQueryVariables = {
      after,
      first,
      ...variables,
    };

    const result = await graphqlClient.request<QueryMemberPickQuery, QueryMemberPickQueryVariables>({
      document: membersQuery,
      variables: pagedVariables,
    });

    return result.members ?? [];
  }

  function renderValueTemplate() {
    if (selectedMember) {
      return <>{selectedMember}</>;
    }

    return <></>;
  }

  function renderRow(member: EmployeePickerMember) {
    return (
      <SimplePickerRow
        value={employeeNameFormatter(member.firstName ?? '', member.lastName ?? '')}
        searchValue={searchValue}
      />
    );
  }

  const scroller = useRef<HTMLElement>();

  const {
    data,
    isLoading,
    isError: isDataError,
    loadedAll,
  } = useReactQueryLazyLoading(scroller.current, [...baseQueryKey, 'employee-picker', variables], getMembers, 10, {
    enabled: isOpen,
  });

  const classes = classNames('employee-picker', className);

  return (
    <DropDownPicker
      value={value}
      dataError={isDataError}
      error={error}
      data={data ?? []}
      loadedAll={loadedAll}
      onSelect={handleSelect}
      onClose={onClose}
      onOpen={() => setIsOpen(true)}
      onSearch={setSearchValue}
      searchValue={searchValue}
      valueTemplate={renderValueTemplate()}
      renderRow={renderRow}
      minWidth={minWidth}
      position={position}
      placeholder={placeholder}
      className={classes}
      setScrollerRef={(scrollerRef) => (scroller.current = scrollerRef)}
      isLoading={isLoading}
    />
  );
}

export default EmployeePicker;
